import { useEffect, useState } from 'react';
import { assistantApi, type ResolvedRef } from '../../api/assistantApi';
import type { AssistantRef } from '../../utils/assistantReferences';

// Answers repeat the same references; resolve each once per page load.
const cache = new Map<string, Promise<ResolvedRef | null>>();

function resolveCached(assistantRef: AssistantRef): Promise<ResolvedRef | null> {
    let pending = cache.get(assistantRef.raw);
    if (!pending) {
        pending = assistantApi.resolveRef(assistantRef).catch((error) => {
            console.error('Error resolving assistant reference:', error);
            cache.delete(assistantRef.raw);
            return null;
        });
        cache.set(assistantRef.raw, pending);
    }
    return pending;
}

export type ResolvedRefState = { status: 'loading' } | { status: 'resolved'; value: ResolvedRef | null };

/** Live name and link for a `{kind:key}` reference in an assistant answer. */
export function useResolvedRef(assistantRef: AssistantRef): ResolvedRefState {
    const [state, setState] = useState<ResolvedRefState>({ status: 'loading' });
    useEffect(() => {
        let cancelled = false;
        resolveCached(assistantRef).then((value) => {
            if (!cancelled) setState({ status: 'resolved', value });
        });
        return () => {
            cancelled = true;
        };
        // raw identifies the reference completely
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assistantRef.raw]);
    return state;
}
